import React from "react";
//Limitless imports
import { fetchFAQSForKnowledeLibrary, searchKnowledgeLibrary, fetchCategoriesForKnowledeLibrary, fetchKnowledgeArticle } from "../../apis/knowledge/KnowledgeAPI";
import { ellipsisText } from "../../utils/DataFormatUtils";
import { useTranslation, useNotificationContext } from "@limitless/common/hooks";
import { useKnowledgeContext } from "./KnowledgeContext";
const INITIAL_STATE = {
  loading: false,
  faqs: [],
  categories: [],
  searchResults: [],
  searchTerm: "",
  selectedCategory: null,
  hasSearched: false,
  searching: false
};
const INITIAL_ARTICLE = {
  loading: false,
  selectedArticleIndex: -1,
  focusArticleAttention: false
};
const SUBCATEGORY_CHAR = "-> ";
const UPDATE_LIBRARY_RESULT = "update-library-result";
const RESET_LIBRARY_RESULT = "reset-library-result";
const CHANGE_CATEGORY = "change-category";
const START_SEARCHING = "start-searching";
const UPDATE_SEARCH_TERM = "update-search-term";
const CLEAR_SEARCHING = "clear-search-term";
const libraryResultReducer = (state, action) => {
  switch (action.type) {
    case UPDATE_LIBRARY_RESULT:
      return {
        ...state,
        ...action.payload
      };
    case CHANGE_CATEGORY:
      return {
        ...state,
        selectedCategory: action.payload,
        hasSearched: false
      };
    case RESET_LIBRARY_RESULT:
      return INITIAL_STATE;
    case START_SEARCHING:
      return {
        ...state,
        hasSearched: true,
        loading: true,
        searchTerm: action.payload,
        selectedCategory: null,
        searching: true
      };
    case CLEAR_SEARCHING:
      return {
        ...state,
        hasSearched: false,
        searchTerm: action.payload
      };
    case UPDATE_SEARCH_TERM:
      return {
        ...state,
        searchTerm: action.payload
      };
    default:
      throw new Error(`Unsupported library result action type ${action.type}`);
  }
};
export const useKnowledgeLibraryResult = customTranslate => {
  const [libraryResults, dispatch] = React.useReducer(libraryResultReducer, INITIAL_STATE);
  const [selectedArticle, setSelectedArticle] = React.useState(INITIAL_ARTICLE);
  const [selectedLibrary, setSelectedLibrary] = React.useState({});
  const {
    t: portalTranslate
  } = useTranslation();
  const {
    addFailureNotification
  } = useNotificationContext();
  const {
    libraries
  } = useKnowledgeContext();
  const [libraryToLoad, setLibraryToLoad] = React.useState();
  React.useEffect(() => {
    if (!libraries.loading && libraryToLoad) {
      const newLibrary = libraries.list.find(item => item.groupName === libraryToLoad.groupName);
      if (newLibrary) {
        updateSelectedLibrary(newLibrary);
      } else {
        updateSelectedLibrary({
          ...libraryToLoad,
          displayName: libraryToLoad.groupName
        });
      }
    }
  }, [libraries.loading, libraryToLoad]);
  const updateSelectedLibrary = library => {
    const t = customTranslate || portalTranslate;
    library.menuTitle = t("Knowledge Library - {0}", ellipsisText(library.displayName, 20));
    setSelectedLibrary(library);
    setSelectedArticle(INITIAL_ARTICLE);
    dispatch({
      type: RESET_LIBRARY_RESULT
    });
    loadCategories(library);
    loadCategoryArticles(library, library.knowledgeCategory);
  };
  const loadCategories = library => {
    fetchCategoriesForKnowledeLibrary(library, categories => {
      dispatch({
        type: UPDATE_LIBRARY_RESULT,
        payload: {
          categories
        }
      });
    });
  };
  const loadCategoryArticles = (library, category, subcategory) => {
    dispatch({
      type: UPDATE_LIBRARY_RESULT,
      payload: {
        loading: true,
        faqs: []
      }
    });
    fetchFAQSForKnowledeLibrary(library, category, subcategory, faqs => {
      dispatch({
        type: UPDATE_LIBRARY_RESULT,
        payload: {
          faqs,
          loading: false,
          searchTerm: ""
        }
      });
    });
  };
  const selectCategory = newCategory => {
    dispatch({
      type: CHANGE_CATEGORY,
      payload: newCategory
    });
    if (newCategory) {
      if (newCategory.label.includes(SUBCATEGORY_CHAR)) {
        const value = newCategory.label.split(SUBCATEGORY_CHAR)[1];
        loadCategoryArticles(selectedLibrary, newCategory.category.category, value);
      } else {
        let category = newCategory.value;
        if (!category) {
          category = selectedLibrary.knowledgeCategory;
        }
        loadCategoryArticles(selectedLibrary, category);
      }
    } else {
      //reload the original FAQs
      loadCategoryArticles(selectedLibrary, selectedLibrary.knowledgeCategory);
    }
  };
  const selectSubCategory = (newCategory, subcategory) => {
    loadCategoryArticles(selectedLibrary, newCategory, subcategory);
  };
  const searchLibrary = newSearchTerm => {
    dispatch({
      type: START_SEARCHING,
      payload: newSearchTerm
    });
    searchKnowledgeLibrary(selectedLibrary, newSearchTerm, results => {
      dispatch({
        type: UPDATE_LIBRARY_RESULT,
        payload: {
          searchResults: results,
          loading: false,
          searching: false
        }
      });
    });
  };
  const updateSelectedArticle = (article, callback) => {
    const {
      selectedArticleIndex,
      focusArticleAttention = false
    } = article;
    setSelectedArticle({
      selectedArticleIndex,
      focusArticleAttention,
      loading: true
    });
    fetchKnowledgeArticle(selectedLibrary, article, fullArticleData => {
      if (fullArticleData) {
        setSelectedArticle({
          ...article,
          ...fullArticleData,
          focusArticleAttention,
          loading: false
        });
        if (typeof callback === "function") {
          callback(fullArticleData);
        }
      } else {
        setSelectedArticle({
          ...article,
          focusArticleAttention,
          loading: false
        });
        addFailureNotification("Failed to fetch article");
      }
    });
  };
  const updateSearchTerm = newTerm => {
    dispatch({
      type: UPDATE_SEARCH_TERM,
      payload: newTerm
    });
  };
  const loadMessageKnowledgeLibrary = message => {
    setLibraryToLoad({
      groupName: message.groupName,
      tenantName: message.tenantName
    });
  };
  const clearSelectedLibrary = () => {
    setSelectedLibrary({});
  };
  const clearSelectedArticle = () => {
    setSelectedArticle(INITIAL_ARTICLE);
  };
  const clearSearchTerm = () => {
    dispatch({
      type: CLEAR_SEARCHING,
      payload: ""
    });
  };
  return {
    libraryResults,
    selectedArticle,
    selectedLibrary,
    updateSelectedLibrary,
    clearSelectedLibrary,
    clearSelectedArticle,
    updateSelectedArticle,
    selectCategory,
    searchLibrary,
    updateSearchTerm,
    SUBCATEGORY_CHAR,
    loadMessageKnowledgeLibrary,
    selectSubCategory,
    loadCategoryArticles,
    clearSearchTerm,
    libraryToLoad,
    setLibraryToLoad
  };
};